import * as Promise from 'bluebird';

/**
 * A guard given to the holder of a @see Mutex.
 */
export interface MutexGuard {
    /**
     * Release the @see Mutex being guarded by this guard, allowing other consumers to acquire the @see Mutex.
     * Releasing an already-released guard has no effect.
     */
    release() : void;
}

// Symbols for use w/in this module only
const
    MUTEX_CURRENT_GUARD = Symbol('mutexCurrentGuard'),
    MUTEX_RELEASE = Symbol('mutexRelease');

/**
 * A mutually exclusive lock for synchronizing concurrent async actions. E.g., say your UI can trigger two kinds
 * of network requests, either request A or request B, but you wanna make sure to avoid sending a request of
 * either type until all requests of the other type have received a response. This utility can help manage that.
 */
export class Mutex {
    // Queue of waiting consumers
    private readonly waiting : WaitingConsumer[] = [];

    [MUTEX_CURRENT_GUARD] : Guard | null = null;

    // TODO: Could switch from taking a timeout as a param to taking something like a cancellation token, to
    // support aborting the wait for reasons other than a timeout expiring. But the timeout-based API should be
    // plenty for now.
    /**
     * Acquire the @see Mutex, returning a promise that resolves with a @see MutexGuard either on the next tick
     * of the event loop (if the @see Mutex is not being held by any other consumers) or when the @see Mutex
     * is released (if the @see Mutex is currently being held by another consumer).
     * @param timeoutMs An optional timeout after which to give up waiting to acquire the @see Mutex. If this
     * timeout is reached before the @see Mutex can be acquired, then the returned promise is rejected with a
     * standard JS error object. Omitting the timeout or passing undefined means "no timeout", i.e. the returned
     * promise will never be rejected but may stay unresolved indefinitely.
     */
    acquire(timeoutMs? : number) {
        return this[MUTEX_CURRENT_GUARD] ?
            new Promise<MutexGuard>((resolve, reject) => this.waiting.push(new WaitingConsumer(resolve, reject, timeoutMs))) :
            Promise.resolve(new Guard(this));
    }

    [MUTEX_RELEASE]() {
        this[MUTEX_CURRENT_GUARD] = null;
        let waiting : WaitingConsumer | undefined;
        do { waiting = this.waiting.shift(); } while (waiting?.hasGivenUp);
        waiting?.resolve(new Guard(this));
    }
}

class Guard implements MutexGuard {
    // nullable and writeable so we can free memory more aggressively
    private mutex : Mutex | null;

    constructor(mutex : Mutex) {
        this.mutex = mutex;
        mutex[MUTEX_CURRENT_GUARD] = this;
    }

    release() {
        if (this.mutex?.[MUTEX_CURRENT_GUARD] === this) {
            this.mutex[MUTEX_RELEASE]();
            this.mutex = null;
        }
    }
}

class WaitingConsumer {
    hasGivenUp = false;

    private readonly timeoutId : number | undefined;
    // nullable and writeable so we can free memory more aggressively
    private _resolve : ((guard : MutexGuard) => void) | null;
    // nullable and writeable so we can free memory more aggressively
    private _reject : ((err : Error) => void) | null;

    constructor(
        resolve : (guard : MutexGuard) => void,
        reject : (err : Error) => void,
        timeoutMs : number | undefined
    ) {
        this._resolve = resolve;
        this._reject = reject;
        this.timeoutId = timeoutMs === undefined ? undefined : setTimeout(() => this.giveUp(), timeoutMs);
    }

    resolve(guard : MutexGuard) {
        this._resolve!.call(null, guard);
        clearTimeout(this.timeoutId);
        this._resolve = null;
        this._reject = null;
    }

    private giveUp() {
        this.hasGivenUp = true;
        this._reject!.call(null, new Error("Mutex could not be acquired within the specified timeout."));
        this._resolve = null;
        this._reject = null;
    }
}



// WEBPACK FOOTER //
// ./lib/mutex.ts